import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getOnlinePlayers } from "@/api/gameApi"
import { ColorRank, OnlinePlayer, RadarSearchMode } from "@/types/battle"
import { useBattleChallenge } from "@/features/battle/BattleChallengeContext"
import { useBattleDeckChallenge } from "@/features/battle/DeckChallengeContext"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { RANK_NAMES } from "@/constants/guardians"
import {
  connectBattleSocket,
  emitBattleEvent,
  onBattleEvent,
} from "@/socket/battleSocket"
import { formatPointsValue } from "@/constants/game.constants"

const RANKS: ColorRank[] = ["WHITE", "GREEN", "BLUE", "PURPLE", "ORANGE", "RED"]

export default function OnlinePlayersPage() {
  const { passport } = useGuardiansPassport()
  const { sendChallenge } = useBattleChallenge()
  const { sendDeckChallenge } = useBattleDeckChallenge()
  const [selected, setSelected] = useState<OnlinePlayer | null>(null)
  const [searchMode, setSearchMode] = useState<RadarSearchMode>("RATING")
  const [rank, setRank] = useState<ColorRank>("WHITE")
  const [deckPicked, setDeckPicked] = useState<string[]>([])

  const q = useQuery({
    queryKey: ["online-players"],
    queryFn: getOnlinePlayers,
    refetchInterval: 20_000,
  })

  useEffect(() => {
    void connectBattleSocket().catch(() => {})
    const refresh = () => emitBattleEvent("online-players")
    refresh()
    const off = onBattleEvent("online-players", () => {
      void q.refetch()
    })
    const t = window.setInterval(refresh, 25_000)
    return () => {
      off()
      window.clearInterval(t)
    }
  }, [q])

  const players = (q.data?.players ?? []).filter(
    (p) => p.userId !== passport?.user_id
  )
  const { collectionCards } = useGuardiansPassport()
  const deckSize = 6

  function toggleDeckCard(id: string) {
    setDeckPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= deckSize) return prev
      return [...prev, id]
    })
  }

  return (
    <GameScreenLayout title="Онлайн" backTo="/play">
      <p className="mb-3 text-xs text-guardians-muted">
        Вызов 1×1 или колодой (6 карт). Нужен battle socket.
      </p>

      {q.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : (
        <ul className="mb-4 space-y-2">
          {players.length === 0 ? (
            <li className="text-sm text-guardians-muted">Никого рядом в списке</li>
          ) : (
            players.map((p) => (
              <li key={p.userId}>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(p)
                    setDeckPicked([])
                  }}
                  className={clsx(
                    "w-full rounded-xl border px-3 py-2 text-left text-sm",
                    selected?.userId === p.userId
                      ? "border-guardians-accent bg-guardians-accent/10"
                      : "border-white/10"
                  )}
                >
                  <span className="font-medium">{p.nickname}</span>
                  <span className="ml-2 text-xs text-guardians-muted">
                    {formatPointsValue(p.globalPts)} · {p.publicGameCode}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      {selected ? (
        <section className="space-y-4 rounded-xl border border-white/10 p-4 text-sm">
          <p>
            Вызов: <strong>{selected.nickname}</strong>
          </p>

          <div>
            <p className="mb-2 text-xs text-guardians-muted">Дуэль 1×1</p>
            <div className="mb-2 flex gap-2">
              <button
                type="button"
                onClick={() => setSearchMode("RATING")}
                className={clsx(
                  "flex-1 rounded-lg py-1.5 text-xs",
                  searchMode === "RATING" ? "bg-guardians-accent text-guardians-bg" : "border border-white/20"
                )}
              >
                Любая карта
              </button>
              <button
                type="button"
                onClick={() => setSearchMode("RANK")}
                className={clsx(
                  "flex-1 rounded-lg py-1.5 text-xs",
                  searchMode === "RANK" ? "bg-guardians-accent text-guardians-bg" : "border border-white/20"
                )}
              >
                По рангу
              </button>
            </div>
            {searchMode === "RANK" ? (
              <select
                value={rank}
                onChange={(e) => setRank(e.target.value as ColorRank)}
                className="mb-2 w-full rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs"
              >
                {RANKS.map((r) => (
                  <option key={r} value={r}>
                    {RANK_NAMES[r]}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              type="button"
              onClick={() =>
                sendChallenge(
                  selected,
                  searchMode,
                  searchMode === "RANK" ? rank : undefined
                )
              }
              className="w-full rounded-xl bg-guardians-accent py-2 text-guardians-bg"
            >
              Отправить вызов
            </button>
          </div>

          <div>
            <p className="mb-2 text-xs text-guardians-muted">
              Колодный вызов ({deckPicked.length}/{deckSize})
            </p>
            <ul className="mb-2 grid max-h-32 grid-cols-2 gap-1 overflow-y-auto text-[10px]">
              {collectionCards.slice(0, 24).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => toggleDeckCard(c.id)}
                    className={clsx(
                      "w-full rounded border px-1 py-1",
                      deckPicked.includes(c.id)
                        ? "border-guardians-accent"
                        : "border-white/10"
                    )}
                  >
                    {c.element} Lv.{c.level}
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              disabled={deckPicked.length !== deckSize}
              onClick={() =>
                sendDeckChallenge(selected.userId, "ANY", deckPicked)
              }
              className="w-full rounded-xl border border-guardians-accent py-2 text-guardians-accent disabled:opacity-40"
            >
              Вызов колодой
            </button>
          </div>
        </section>
      ) : null}
    </GameScreenLayout>
  )
}
